import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import {
  AuthArgs,
  SearchConfigService,
  WeightMap,
  WeightsView,
} from "../services/search-config.service";

const GROUP_LABELS: Record<string, string> = {
  pt: "Trainers (search results)",
  gym: "Gyms (search results)",
  service: "Packages / services (search results)",
  post: "Posts (search results)",
  for_you: "Trainers for you (current ranking)",
  for_you_v3: "Trainers for you (candidate ranking, gradual rollout)",
};

const COMPONENT_HELP: Record<string, string> = {
  keyword: "How well the text matches the query",
  rating: "Review rating, adjusted for how few reviews there are",
  trainer_rating: "The trainer's rating",
  distance: "Closer to the user scores higher",
  verified: "Verified account or package",
  popularity: "Number of reviews",
  market: "Same market as the user",
  open_now: "Open at this moment",
  badge: "Has a listing badge",
  recency: "Newer scores higher",
  engagement: "Reactions and comments",
  author_trainer: "Written by a trainer",
  completeness: "How complete the profile is",
  preference: "Matches the user's saved filter",
  goal: "Matches the user's fitness goal",
  freshness: "Recently joined trainers",
};

const MAX_WEIGHT = 100;

// Keep inputs as strings so typing "0." or clearing a field does not fight the user.
type Draft = Record<string, Record<string, string>>;

const toDraft = (w: WeightMap): Draft =>
  Object.fromEntries(
    Object.entries(w).map(([g, parts]) => [
      g,
      Object.fromEntries(Object.entries(parts).map(([k, v]) => [k, String(v)])),
    ]),
  );

const parse = (s: string): number | null => {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 && n <= MAX_WEIGHT ? n : null;
};

export default function WeightsPanel() {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const args: AuthArgs = { auth, setAuth, logout };

  const [view, setView] = useState<WeightsView | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const apply = (v: WeightsView) => {
    setView(v);
    setDraft(toDraft(v.effective));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      apply(await SearchConfigService.getWeights({ auth, setAuth, logout }));
    } catch (e: any) {
      setError(e?.message || "Failed to load weights");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  useEffect(() => {
    load();
  }, [load]);

  /** Problems that would make the server reject the save, per group. */
  const problems = useMemo(() => {
    const out: string[] = [];
    if (!view) return out;
    for (const g of view.groups) {
      const parts = draft[g] ?? {};
      const values = Object.entries(parts).map(([k, s]) => [k, parse(s)] as const);
      for (const [k, v] of values)
        if (v === null) out.push(`${g}.${k} must be a number from 0 to ${MAX_WEIGHT}`);
      if (values.length > 0 && values.every(([, v]) => v === 0))
        out.push(`${GROUP_LABELS[g] ?? g} needs at least one weight above 0`);
    }
    return out;
  }, [draft, view]);

  const dirty = useMemo(() => {
    if (!view) return false;
    return view.groups.some((g) =>
      Object.entries(draft[g] ?? {}).some(
        ([k, s]) => parse(s) !== view.effective[g][k],
      ),
    );
  }, [draft, view]);

  const save = async () => {
    if (!view || problems.length > 0) return;
    // Only what differs from the defaults is stored, so later changes to defaults still apply elsewhere.
    const overrides: WeightMap = {};
    for (const g of view.groups) {
      for (const [k, s] of Object.entries(draft[g] ?? {})) {
        const v = parse(s);
        if (v !== null && v !== view.defaults[g][k]) {
          (overrides[g] ??= {})[k] = v;
        }
      }
    }
    setSaving(true);
    setError(null);
    try {
      apply(await SearchConfigService.saveWeights(args, overrides));
      setNotice("Saved. Search picks this up within about a minute.");
      setTimeout(() => setNotice(null), 5000);
    } catch (e: any) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset every weight to the built-in defaults?")) return;
    setSaving(true);
    setError(null);
    try {
      apply(await SearchConfigService.resetWeights(args));
      setNotice("Back to defaults.");
      setTimeout(() => setNotice(null), 5000);
    } catch (e: any) {
      setError(e?.message || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const resetGroup = (g: string) =>
    view && setDraft({ ...draft, [g]: toDraft({ [g]: view.defaults[g] })[g] });

  if (!view) {
    return (
      <div>
        {error && <div className="alert alert-danger">{error}</div>}
        {loading && <div className="text-muted">Loading…</div>}
      </div>
    );
  }

  return (
    <div>
      <p className="text-muted small">
        Weights are relative: only their ratio matters. Each result's score is
        the weighted average of its signals, and a signal that is missing (for
        example distance when the user's location is unknown) is left out
        without lowering the score. Set a weight to 0 to ignore a signal.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      {problems.length > 0 && (
        <div className="alert alert-warning">
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </div>
      )}

      {view.groups.map((g) => {
        const parts = draft[g] ?? {};
        const sum = Object.values(parts).reduce((a, s) => a + (parse(s) ?? 0), 0);
        return (
          <div className="card shadow-sm border-0 mb-4" key={g}>
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <strong>{GROUP_LABELS[g] ?? g}</strong>
              <button
                className="btn btn-sm btn-link"
                onClick={() => resetGroup(g)}
              >
                Reset group
              </button>
            </div>
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4">Signal</th>
                    <th style={{ width: 110 }}>Default</th>
                    <th style={{ width: 140 }}>Weight</th>
                    <th style={{ width: 110 }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(view.defaults[g]).map((k) => {
                    const v = parse(parts[k] ?? "");
                    const share = sum > 0 && v !== null ? (v / sum) * 100 : 0;
                    return (
                      <tr key={k}>
                        <td className="ps-4">
                          <div className="font-monospace">{k}</div>
                          <div className="small text-muted">
                            {COMPONENT_HELP[k] ?? ""}
                          </div>
                        </td>
                        <td className="text-muted">{view.defaults[g][k]}</td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min={0}
                            max={MAX_WEIGHT}
                            className={`form-control form-control-sm ${
                              v === null ? "is-invalid" : ""
                            }`}
                            value={parts[k] ?? ""}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                [g]: { ...parts, [k]: e.target.value },
                              })
                            }
                          />
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="progress flex-grow-1"
                              style={{ height: 6 }}
                            >
                              <div
                                className="progress-bar"
                                style={{ width: `${share}%` }}
                              />
                            </div>
                            <span className="small">{share.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <div className="d-flex align-items-center gap-3">
        <button
          className="btn btn-primary"
          disabled={!dirty || problems.length > 0 || saving}
          onClick={save}
        >
          {saving ? "Saving…" : "Save weights"}
        </button>
        <button
          className="btn btn-outline-danger"
          disabled={saving}
          onClick={resetAll}
        >
          Reset all to defaults
        </button>
        {view.updated_at && (
          <span className="small text-muted">
            Last changed {new Date(view.updated_at).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}
